/**
 * Conversation Transcript Component
 *
 * Displays a diarized conversation with doctor/patient bubbles
 * and a live area for provisional transcription.
 */

import { TutorialAnchor } from '@/features/session-tutorial/TutorialAnchor';
import { DEFAULT_SPEAKER_TITLES, type SpeakerTitles } from '@/lib/speakerTitles';
import { cn } from '@/lib/utils';
import { Loader2, Mic, Stethoscope, User } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { RecordingEmptyState } from './RecordingEmptyState';

export interface ConversationMessage {
  id: string;
  speaker: 'doctor' | 'patient';
  text: string;
  timestamp: number; // seconds from start
  end_timestamp?: number; // end time in seconds (for audio seeking)
  isProvisional?: boolean;
  isPendingRefinement?: boolean; // True when instant model emitted, waiting for large model
  wasJustRefined?: boolean; // True briefly when text was updated by large model
}

interface ConversationTranscriptProps {
  messages: ConversationMessage[];
  provisionalText: string;
  isRecording: boolean;
  /** Recording session open but capture paused — the live area shows "Bíður..." */
  isPaused?: boolean;
  className?: string;
  /** Segment IDs to highlight (from summary hover) */
  highlightedSegmentIds?: string[] | null;
  /** True while connecting/initializing recording */
  isInitializing?: boolean;
  /** True while finalizing transcription after stopping */
  isFinalizingTranscription?: boolean;
  onStartRecording: () => void;
  isRecordingDisabled?: boolean;
  recordingDisabledReason?: string;
  /** Speaker names; follow the user's profession (e.g. Félagsráðgjafi / Skjólstæðingur) */
  speakerTitles?: SpeakerTitles;
}

// Format seconds to MM:SS
const formatTimestamp = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Speaker bubble component
function SpeakerBubble({
  message,
  isHighlighted,
  speakerTitles,
}: {
  message: ConversationMessage;
  isHighlighted?: boolean;
  speakerTitles: SpeakerTitles;
}) {
  const isDoctor = message.speaker === 'doctor';
  const isPending = message.isPendingRefinement;
  const wasRefined = message.wasJustRefined;

  return (
    <div
      className={cn(
        'fade-in slide-in-from-bottom-2 flex animate-in gap-3 duration-300',
        isDoctor ? 'flex-row' : 'flex-row-reverse'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200',
          isDoctor ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600',
          // Highlight avatar when segment is highlighted
          isHighlighted && 'scale-110 ring-2 ring-blue-400'
        )}
      >
        {isDoctor ? <Stethoscope className="h-4 w-4" /> : <User className="h-4 w-4" />}
      </div>

      {/* Message bubble */}
      <div className={cn('max-w-[80%] flex-1', isDoctor ? 'mr-auto' : 'ml-auto')}>
        {/* Header */}
        <div className={cn('mb-1 flex items-center gap-2', isDoctor ? 'flex-row' : 'flex-row-reverse')}>
          <span className="font-medium text-slate-600 text-xs">
            {isDoctor ? speakerTitles.clinician : speakerTitles.counterpart}
          </span>
          <span className="text-slate-400 text-xs">{formatTimestamp(message.timestamp)}</span>
          {/* Pending refinement indicator */}
          {isPending && (
            <span className="flex items-center gap-1 text-slate-400 text-xs" title="Fínpússun í vinnslu...">
              <Loader2 className="h-3 w-3 animate-spin" />
            </span>
          )}
        </div>

        {/* Bubble */}
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm leading-relaxed transition-all duration-200',
            isDoctor ? 'rounded-tl-sm bg-blue-50' : 'rounded-tr-sm bg-emerald-50',
            // Text color: slightly muted when pending, full when confirmed
            isPending ? 'text-slate-600' : 'text-slate-800',
            // Highlight animation when text was just refined
            wasRefined && 'ring-2 ring-amber-300 ring-opacity-75',
            // Highlight when segment is selected from summary hover
            isHighlighted && 'bg-blue-100/50 shadow-md ring-2 ring-blue-400'
          )}
        >
          {message.text}
        </div>
      </div>
    </div>
  );
}

// Live transcription area (no speaker assigned yet)
function LiveArea({
  text,
  isRecording,
  isListening,
}: {
  text: string;
  isRecording: boolean;
  isListening: boolean;
}) {
  if (!text && !isRecording) return null;

  return (
    <div className="mt-4 border-slate-200 border-t border-dashed pt-4">
      <div className="mb-2 flex items-center gap-2">
        <div className={cn('h-2 w-2 rounded-full', isListening ? 'animate-pulse bg-red-500' : 'bg-slate-300')} />
        <span className="font-medium text-slate-500 text-xs">{isListening ? 'Hlustar...' : 'Bíður...'}</span>
      </div>

      {text ? (
        <div className="flex gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100">
            <Mic className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex-1 rounded-2xl bg-slate-100 px-4 py-2.5 text-slate-600 text-sm italic">{text}</div>
        </div>
      ) : isListening ? (
        <div className="flex gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100">
            <Mic className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex-1 rounded-2xl bg-slate-50 px-4 py-2.5 text-slate-400 text-sm italic">
            Talaðu við hljóðnemann...
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Initializing state - shown while connecting
function InitializingState() {
  return (
    <div className="flex h-full flex-col items-center justify-center p-8 text-center text-slate-500">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
      </div>
      <h3 className="mb-2 font-medium text-lg text-slate-600">Tengist...</h3>
      <p className="max-w-sm text-slate-400 text-sm">
        Undirbý upptöku. Bíddu þangað til þetta hverfur áður en þú byrjar að tala.
      </p>
    </div>
  );
}

// Finalizing state - shown while processing after recording stops
function FinalizingState() {
  return (
    <div className="flex h-full flex-col items-center justify-center p-8 text-center text-slate-500">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
      </div>
      <h3 className="mb-2 font-medium text-lg text-slate-600">Vinn úr upptöku...</h3>
      <p className="max-w-sm text-slate-400 text-sm">Klára umritun og greina ræðumenn. Þetta getur tekið smá stund.</p>
    </div>
  );
}

export function ConversationTranscript({
  messages,
  provisionalText,
  isRecording,
  isPaused = false,
  className,
  highlightedSegmentIds,
  isInitializing = false,
  isFinalizingTranscription = false,
  onStartRecording,
  isRecordingDisabled,
  recordingDisabledReason,
  speakerTitles = DEFAULT_SPEAKER_TITLES,
}: ConversationTranscriptProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const followTranscriptRef = useRef(true);
  // Store refs for each message element (for scroll-to-highlight)
  const messageRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

  const setScrollContainer = useCallback((container: HTMLDivElement | null) => {
    scrollContainerRef.current = container;
    // A newly mounted viewport starts by showing the latest transcript.
    followTranscriptRef.current = true;
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    followTranscriptRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
  }, []);

  // Remember the user's position before content grows. Measuring after the
  // update mistakes a large new bubble for the user having scrolled up.
  // Move only this viewport, before paint, so iframe ancestors stay put.
  // biome-ignore lint/correctness/useExhaustiveDependencies: these props change the transcript's rendered height
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (container && followTranscriptRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, provisionalText, isRecording, isPaused, isInitializing, isFinalizingTranscription]);

  // Auto-scroll to highlighted segment when hovering over summary
  useEffect(() => {
    if (!highlightedSegmentIds || highlightedSegmentIds.length === 0) return;

    // Find the first highlighted message element
    const firstHighlightedId = highlightedSegmentIds[0];
    const element = messageRefsMap.current.get(firstHighlightedId);

    if (element) {
      // Scroll into view with smooth behavior, centered in viewport
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedSegmentIds]);

  // Memoize filtered messages to avoid re-computation on every render
  const filteredMessages = useMemo(() => {
    return (
      messages
        .map((message) => ({
          ...message,
          // Remove [silence] from text
          text: message.text.replace(/\[silence\]/gi, '').trim(),
        }))
        // Filter out messages that are now empty (were only [silence])
        .filter((message) => message.text.length > 0)
    );
  }, [messages]);

  // Clean up stale refs when messages change
  useEffect(() => {
    const currentIds = new Set(filteredMessages.map((m) => m.id));
    messageRefsMap.current.forEach((_, id) => {
      if (!currentIds.has(id)) {
        messageRefsMap.current.delete(id);
      }
    });
  }, [filteredMessages]);

  const hasContent = filteredMessages.length > 0 || provisionalText.trim().length > 0 || isRecording;

  // Memoize the Set for O(1) lookup of highlighted segment IDs
  const highlightedSet = useMemo(() => new Set(highlightedSegmentIds || []), [highlightedSegmentIds]);

  // Callback ref setter to avoid creating new function on each render
  const setMessageRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) {
        messageRefsMap.current.set(id, el);
      } else {
        messageRefsMap.current.delete(id);
      }
    },
    []
  );

  // Show finalizing state only when there's no content yet
  const showFinalizingState = isFinalizingTranscription && !hasContent;

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      {isInitializing ? (
        <InitializingState />
      ) : showFinalizingState ? (
        <FinalizingState />
      ) : !hasContent ? (
        <RecordingEmptyState
          onStart={onStartRecording}
          disabled={isRecordingDisabled}
          disabledReason={recordingDisabledReason}
          desktopPrompt="Notaðu upptökuhnappinn hér að ofan til að hefja viðtalið."
        />
      ) : (
        <TutorialAnchor step="demo">
          <div ref={setScrollContainer} onScroll={handleScroll} className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <div className="mx-auto max-w-3xl space-y-4">
              {filteredMessages.map((message) => (
                <div key={message.id} ref={setMessageRef(message.id)}>
                  <SpeakerBubble
                    message={message}
                    isHighlighted={highlightedSet.has(message.id)}
                    speakerTitles={speakerTitles}
                  />
                </div>
              ))}

              <LiveArea text={provisionalText} isRecording={isRecording} isListening={isRecording && !isPaused} />

              <div />
            </div>
          </div>
        </TutorialAnchor>
      )}
    </div>
  );
}
